import {RefObject, useEffect} from "react";
import {getHtmlElement} from "./getHtmlElement";

/**
 * Publishes this region's own vertical-scrollbar metrics as CSS custom properties,
 * so the scroll cue and the right-edge spacing can react to the *actual,
 * per-element* scrollbar — something no global variable or pure-CSS rule can know
 * (the same table gains or loses its bar as its content or height changes).
 *
 *  - `--table-scrollbar-inset`: the width the vertical scrollbar currently occupies
 *    (`offsetWidth - clientWidth`; the region has no inline border, so this is
 *    exactly padding-box-right minus scrollport-right — i.e. 0 when no bar is
 *    shown). `.table--fixed` insets its `scroll`-positioned right shadow by this so
 *    the cue lands on the content's right edge instead of behind the bar.
 *
 *  - `--table-vbar`: 1 when a vertical scrollbar is present, else 0. Apps use it as
 *    a boolean to switch the region's right edge between an empty margin (no bar,
 *    mirroring the left inset of #root) and letting the bar occupy that space (bar
 *    present). See mbasico main.less.
 *
 * NOTE: for these metrics to reflect the *real* bar, the element must NOT have
 * `scrollbar-gutter: stable` — that always reserves the gutter, which would pin the
 * inset to the scrollbar width and `--table-vbar` to 1 even with no bar. mbasico
 * overrides it to `auto` on these regions for exactly this reason.
 */
function apply(element: HTMLElement, prev: { barWidth: number }): void {
	// offsetWidth - clientWidth is the vertical scrollbar width; 0 when none.
	const barWidth = element.offsetWidth - element.clientWidth;
	if (barWidth === prev.barWidth) return; // idempotent — avoids a ResizeObserver feedback loop
	prev.barWidth = barWidth;

	element.style.setProperty("--table-scrollbar-inset", `${barWidth}px`);
	element.style.setProperty("--table-vbar", barWidth > 0 ? "1" : "0");
}

export function useTableScrollbarMetrics(ref: RefObject<HTMLElement | null>): void {
	useEffect(() => {
		const element = getHtmlElement(ref);
		if (!element) return;

		// -1 forces the first measurement to apply (a real width is never negative).
		const prev = {barWidth: -1};

		// Fires whenever the content box changes size, including when a vertical
		// scrollbar appears/disappears (clientWidth changes). Writing only custom
		// properties cannot itself resize the box, and the early-return guard drops
		// redundant fires, so there is no observer feedback loop.
		const observer = new ResizeObserver(() => apply(element, prev));
		observer.observe(element);
		apply(element, prev);

		return () => observer.disconnect();
	}, [ref]);
}
